import React, { useEffect, useState } from 'react';
import { usePaginacion, Paginacion } from '../../../Helpers/importacionesComunes'
import { Card, CardBody, Typography, Button } from '@material-tailwind/react';
import { ChevronUpDownIcon } from '@heroicons/react/24/solid';
import { PlusIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import ExportToExcelGeneric from '../../../componentes/exportExcelGeneric';
import { ListadoCelular } from './ListadoCelular';
import { useContextoGlobal } from '../../../Context/contextoGlobal'

const badgeClassName = "inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs font-semibold tracking-wide shadow-sm"

const esSedeCentral = (item) => Number(item?.sede_admin) === 1
const esActivo = (item) => String(item?.estado || '').trim().toLowerCase() === 'activo'
const cobraEventos = (item) => Number(item?.cobra_eventos) === 1

const mapearParaExcel = (registros) => registros.map(item => ({
    'Nombre': item.nombre ?? '',
    'Usuario': item.nombre_usuario ?? '',
    'Región': esSedeCentral(item) ? '' : (item.id_region ?? ''),
    'Tipo': esSedeCentral(item) ? 'Sede Central' : 'Secretario regional',
    'Perfil': item.rol_sede ?? '',
    'Cobra eventos': cobraEventos(item) ? 'SÍ' : 'NO',
    'Rol contabilidad': item.rol_contabilidad ?? '',
    'Estado': item.estado ?? '',
}))

const CAMPOS_NUMERICOS = ['id_region', 'rol_contabilidad', 'sede_admin', 'cobra_eventos']

export const Tabla = ({ registrosAmostrar, reinicializar, onCrearUsuario, onEditarUsuario }) => {

    const { configuracion, paginar, inicializar: inicializarPaginacion } = usePaginacion()
    const [copiaRegistrosaMostrar, setCopiaRegistrosaMostrar] = useState([...registrosAmostrar])
    const [orden, setOrden] = useState(null)
    const [nuevoCampo, setNuevoCampo] = useState(true)
    const [contadorOrden, setContadorOrden] = useState(0)
    const { esCelular } = useContextoGlobal()

    useEffect(() => {
        setCopiaRegistrosaMostrar([...registrosAmostrar])
        inicializarPaginacion(null, registrosAmostrar)
    }, [registrosAmostrar])

    useEffect(() => {
        if (orden) {
            setCopiaRegistrosaMostrar(prev => [...prev].sort(comparacion))
        }
    }, [contadorOrden])

    const funcionOrden = (nombre_campo) => {
        setNuevoCampo(orden !== nombre_campo)
        setOrden(nombre_campo)
        setContadorOrden(contadorOrden + 1)
    }

    const comparacion = (a, b) => {
        if (!orden) return 0

        const descendente = !nuevoCampo && contadorOrden % 2 === 0

        if (CAMPOS_NUMERICOS.includes(orden)) {
            const na = Number(a[orden] ?? 0)
            const nb = Number(b[orden] ?? 0)
            return descendente ? nb - na : na - nb
        }

        const ta = String(a[orden] ?? '')
        const tb = String(b[orden] ?? '')
        return descendente ? tb.localeCompare(ta) : ta.localeCompare(tb)
    }

    if (esCelular) {
        return <ListadoCelular datos={copiaRegistrosaMostrar} funcionSeleccion={onEditarUsuario} />
    }

    return <Card className="h-full w-full max-w-full overflow-hidden">
        {copiaRegistrosaMostrar.length > 0 &&
            <div className="flex flex-wrap items-center justify-between gap-3 px-3 pt-3 pb-4 sm:px-4">
                <Paginacion configuracion={configuracion} paginar={paginar} />
                <div className='ml-auto flex items-center gap-2'>
                    <ExportToExcelGeneric data={mapearParaExcel(copiaRegistrosaMostrar)} fileName="Listado de Usuarios" />
                    <Button
                        onClick={() => onCrearUsuario && onCrearUsuario()}
                        variant="outlined"
                        className='flex items-center gap-2 bg-[#2FB18F] text-white'
                    >
                        <PlusIcon className="h-4 w-4" />
                        Añadir
                    </Button>
                </div>
            </div>
        }

        <CardBody className="w-full overflow-x-auto px-0 pt-0 pb-0">
            <table className="table-auto text-left w-full min-w-[64rem]">
                <thead>
                    <tr>
                        <ThCell><ThTitle title='Nombre' sort orden={orden === 'nombre'} callback={() => funcionOrden('nombre')} /></ThCell>
                        <ThCell><ThTitle title='Usuario' sort orden={orden === 'nombre_usuario'} callback={() => funcionOrden('nombre_usuario')} /></ThCell>
                        <ThCell><ThTitle title='Región' sort orden={orden === 'id_region'} callback={() => funcionOrden('id_region')} /></ThCell>
                        <ThCell><ThTitle title='Tipo' sort orden={orden === 'sede_admin'} callback={() => funcionOrden('sede_admin')} /></ThCell>
                        <ThCell><ThTitle title='Perfil' sort orden={orden === 'rol_sede'} callback={() => funcionOrden('rol_sede')} /></ThCell>
                        <ThCell><ThTitle title='Cobra eventos' sort orden={orden === 'cobra_eventos'} callback={() => funcionOrden('cobra_eventos')} /></ThCell>
                        <ThCell><ThTitle title='Rol contabilidad' sort orden={orden === 'rol_contabilidad'} callback={() => funcionOrden('rol_contabilidad')} /></ThCell>
                        <ThCell><ThTitle title='Estado' sort orden={orden === 'estado'} callback={() => funcionOrden('estado')} /></ThCell>
                        <ThCell><ThTitle title='Acciones' /></ThCell>
                    </tr>
                </thead>
                <tbody>
                    {copiaRegistrosaMostrar
                        .filter((item, index) => index >= configuracion.iIni && index <= configuracion.iFin)
                        .map((item, index) => {
                            const clases = "p-4 border-b border-blue-gray-50"

                            return <tr key={item.id_usuario ?? `usuario-${index}`} className="hover:bg-blue-gray-50/40 transition-colors">
                                <td className={clases}>
                                    <Typography variant="small" color="blue-gray" className="font-medium">
                                        {item.nombre || '--'}
                                    </Typography>
                                </td>
                                <td className={clases}>
                                    <Typography variant="small" className="font-normal text-gray-600">
                                        {item.nombre_usuario || '--'}
                                    </Typography>
                                </td>
                                <td className={clases}>
                                    <Typography variant="small" className="font-normal text-gray-600">
                                        {esSedeCentral(item) ? '--' : (item.id_region ?? '--')}
                                    </Typography>
                                </td>
                                <td className={clases}>
                                    <span className={`${badgeClassName} ${esSedeCentral(item) ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-gray-100 text-gray-700 border border-gray-200'}`}>
                                        {esSedeCentral(item) ? 'Sede Central' : 'Regional'}
                                    </span>
                                </td>
                                <td className={clases}>
                                    <Typography variant="small" className="font-normal text-gray-600">
                                        {item.rol_sede || '--'}
                                    </Typography>
                                </td>
                                <td className={clases}>
                                    <span className={`${badgeClassName} ${cobraEventos(item) ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                                        {cobraEventos(item) ? 'SÍ' : 'NO'}
                                    </span>
                                </td>
                                <td className={clases}>
                                    <Typography variant="small" className="font-normal text-gray-600">
                                        {item.rol_contabilidad ?? '--'}
                                    </Typography>
                                </td>
                                <td className={clases}>
                                    <span className={`${badgeClassName} ${esActivo(item) ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                                        {item.estado || '--'}
                                    </span>
                                </td>
                                <td className={clases}>
                                    <button
                                        type="button"
                                        title="Editar usuario"
                                        onClick={() => onEditarUsuario && onEditarUsuario(item)}
                                        className="text-blue-500 hover:text-blue-700 transition-colors"
                                    >
                                        <PencilSquareIcon className="h-5 w-5" />
                                    </button>
                                </td>
                            </tr>
                        })}
                </tbody>
            </table>

            {copiaRegistrosaMostrar.length === 0 &&
                <div className="py-10 text-center text-gray-400 text-sm italic">
                    No se encontraron usuarios
                </div>}
        </CardBody>

        {copiaRegistrosaMostrar.length > 0 &&
            <div className="flex justify-center px-3 py-4">
                <Paginacion configuracion={configuracion} paginar={paginar} />
            </div>}
    </Card>
}

const ThCell = ({ children }) => (
    <th className="cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-4 transition-colors hover:bg-blue-gray-50">
        {children}
    </th>
)

const ThTitle = ({ title, sort, orden, callback }) => {
    return <div className='flex f-col justify-center items-center'>
        <Typography
            variant="small"
            color="blue-gray"
            className="flex justify-between gap-2 font-normal leading-none opacity-70"
        >
            {title}{" "}
            {sort &&
                <ChevronUpDownIcon strokeWidth={2} className={`cursor-pointer ${orden ? 'text-blue-500' : ''} h-4 w-4`} onClick={callback} />
            }
        </Typography>
    </div>
}
